import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FaBan,
  FaVolumeMute,
  FaCheckCircle,
  FaTimesCircle,
  FaUserShield,
  FaHistory,
  FaExclamationTriangle,
  FaFilter,
  FaSearch,
  FaChartBar,
  FaTimes,
  FaArrowLeft,
  FaCalendarAlt,
  FaClock,
  FaShieldAlt,
  FaFire,
  FaChevronDown,
  FaEye,
  FaCopy,
} from "react-icons/fa";
import toast, { Toaster } from "react-hot-toast";

// --- ENHANCED STYLES ---
const PageStyles = () => (
  <style>{`
    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(20px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes slideIn {
      from { opacity: 0; transform: translateX(-20px); }
      to { opacity: 1; transform: translateX(0); }
    }
    @keyframes shimmer {
      0% { background-position: -1000px 0; }
      100% { background-position: 1000px 0; }
    }
    @keyframes glow {
      0%, 100% { box-shadow: 0 0 20px rgba(139, 92, 246, 0.3); }
      50% { box-shadow: 0 0 30px rgba(139, 92, 246, 0.5); }
    }
    @keyframes float {
      0%, 100% { transform: translateY(0px); }
      50% { transform: translateY(-4px); }
    }
    .animate-fade-in {
      animation: fadeIn 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
      opacity: 0;
    }
    .animate-slide-in {
      animation: slideIn 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
    }
    .animate-float {
      animation: float 3s ease-in-out infinite;
    }
    .glass-card {
      background: linear-gradient(135deg, rgba(30, 30, 45, 0.6), rgba(20, 20, 35, 0.4));
      backdrop-filter: blur(20px) saturate(180%);
      -webkit-backdrop-filter: blur(20px) saturate(180%);
      border: 1px solid rgba(255, 255, 255, 0.08);
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
    }
    .glass-card-hover {
      transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
    }
    .glass-card-hover:hover {
      border-color: rgba(139, 92, 246, 0.3);
      transform: translateY(-2px);
      box-shadow: 0 12px 40px rgba(139, 92, 246, 0.15);
    }
    .gradient-border {
      position: relative;
      background: linear-gradient(135deg, rgba(30, 30, 45, 0.6), rgba(20, 20, 35, 0.4));
    }
    .gradient-border::before {
      content: '';
      position: absolute;
      inset: 0;
      padding: 1px;
      background: linear-gradient(135deg, rgba(139, 92, 246, 0.3), rgba(59, 130, 246, 0.1), transparent);
      border-radius: inherit;
      mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
      -webkit-mask-composite: xor;
      mask-composite: exclude;
      pointer-events: none;
    }
    .shimmer {
      background: linear-gradient(90deg, rgba(55, 65, 81, 0.4) 0%, rgba(75, 85, 99, 0.6) 50%, rgba(55, 65, 81, 0.4) 100%);
      background-size: 1000px 100%;
      animation: shimmer 2s infinite linear;
    }
    .scrollbar-thin::-webkit-scrollbar {
      width: 6px;
      height: 6px;
    }
    .scrollbar-thin::-webkit-scrollbar-track {
      background: rgba(31, 41, 55, 0.3);
      border-radius: 10px;
    }
    .scrollbar-thin::-webkit-scrollbar-thumb {
      background: linear-gradient(180deg, rgba(139, 92, 246, 0.5), rgba(59, 130, 246, 0.5));
      border-radius: 10px;
    }
    .scrollbar-thin::-webkit-scrollbar-thumb:hover {
      background: linear-gradient(180deg, rgba(139, 92, 246, 0.8), rgba(59, 130, 246, 0.8));
    }
    .glow-red {
      filter: drop-shadow(0 0 8px rgba(239, 68, 68, 0.5));
    }
    .glow-yellow {
      filter: drop-shadow(0 0 8px rgba(234, 179, 8, 0.5));
    }
    .glow-green {
      filter: drop-shadow(0 0 8px rgba(34, 197, 94, 0.5));
    }
    .status-dot {
      position: relative;
      display: inline-block;
    }
    .status-dot::before {
      content: '';
      position: absolute;
      inset: -4px;
      border-radius: 50%;
      background: inherit;
      opacity: 0.4;
      animation: ping 2s cubic-bezier(0, 0, 0.2, 1) infinite;
    }
    @keyframes ping {
      75%, 100% { transform: scale(1.8); opacity: 0; }
    }
    .noise-bg {
      background-image: 
        radial-gradient(ellipse 80% 60% at 50% -10%, rgba(139, 92, 246, 0.15), transparent),
        radial-gradient(ellipse 60% 50% at 80% 50%, rgba(59, 130, 246, 0.08), transparent),
        radial-gradient(ellipse 60% 50% at 20% 80%, rgba(236, 72, 153, 0.06), transparent);
    }
  `}</style>
);

// --- ENHANCED DONUT CHART ---
const DonutChart = ({ data }) => {
  let cumulativePercent = 0;
  const radius = 15.91549430918954;
  const total = data.reduce((acc, curr) => acc + curr.count, 0);

  return (
    <div className="relative w-36 h-36 sm:w-44 sm:h-44 mx-auto">
      <svg viewBox="0 0 42 42" className="w-full h-full transform -rotate-90">
        <defs>
          {data.map((slice, i) => (
            <linearGradient key={i} id={`grad-${i}`} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={slice.color} stopOpacity="1" />
              <stop offset="100%" stopColor={slice.color} stopOpacity="0.6" />
            </linearGradient>
          ))}
        </defs>
        <circle cx="21" cy="21" r={radius} fill="transparent" stroke="rgba(55, 65, 81, 0.3)" strokeWidth="4" />
        {data.map((slice, i) => {
          const strokeDasharray = `${slice.percent} ${100 - slice.percent}`;
          const strokeDashoffset = -cumulativePercent;
          cumulativePercent += slice.percent;
          return (
            <circle
              key={i}
              cx="21" cy="21" r={radius}
              fill="transparent"
              stroke={`url(#grad-${i})`}
              strokeWidth="4"
              strokeDasharray={strokeDasharray}
              strokeDashoffset={strokeDashoffset}
              strokeLinecap="round"
              className="transition-all duration-1000 ease-out"
              style={{ filter: `drop-shadow(0 0 6px ${slice.color}80)` }}
            />
          );
        })}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
        <span className="text-3xl font-black bg-gradient-to-br from-white to-gray-400 bg-clip-text text-transparent">
          {total}
        </span>
        <span className="text-[10px] text-gray-500 font-bold uppercase tracking-widest mt-1">Total</span>
      </div>
    </div>
  );
};

// --- ENHANCED BAR CHART ---
const ReasonBarChart = ({ reasons }) => {
  const maxCount = Math.max(...reasons.map(r => r.count));
  const colors = [
    ["#8b5cf6", "#6366f1"], ["#06b6d4", "#0ea5e9"], ["#10b981", "#14b8a6"],
    ["#f59e0b", "#f97316"], ["#ef4444", "#ec4899"], ["#ec4899", "#d946ef"],
    ["#6366f1", "#8b5cf6"], ["#14b8a6", "#06b6d4"], ["#f97316", "#f59e0b"],
    ["#a855f7", "#8b5cf6"]
  ];

  return (
    <div className="space-y-3 max-h-96 overflow-y-auto scrollbar-thin pr-2">
      {reasons.map((reason, idx) => (
        <div key={idx} className="animate-slide-in group" style={{ animationDelay: `${idx * 0.04}s` }}>
          <div className="flex justify-between items-center text-xs mb-1.5">
            <span 
              className="text-gray-300 font-medium truncate pr-2 flex-1 group-hover:text-white transition-colors" 
              title={reason.name}
            >
              {reason.name}
            </span>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="text-white font-bold text-sm">{reason.count}</span>
              <span className="text-gray-500 text-[10px] font-mono">
                {reason.percent.toFixed(1)}%
              </span>
            </div>
          </div>
          <div className="relative w-full bg-gray-900/50 rounded-full h-2 overflow-hidden">
            <div 
              className="absolute inset-y-0 left-0 rounded-full transition-all duration-1000 ease-out"
              style={{ 
                width: `${(reason.count / maxCount) * 100}%`,
                background: `linear-gradient(90deg, ${colors[idx % colors.length][0]}, ${colors[idx % colors.length][1]})`,
                boxShadow: `0 0 10px ${colors[idx % colors.length][0]}60`
              }}
            ></div>
          </div>
        </div>
      ))}
    </div>
  );
};

// --- ENHANCED FILTER PANEL ---
const FilterPanel = ({ filters, setFilters, stats, onReset }) => {
  const [isOpen, setIsOpen] = useState(false);
  const activeCount = [filters.type, filters.status, filters.search].filter(Boolean).length;

  return (
    <div className="mb-6">
      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`group flex items-center gap-2.5 px-5 py-2.5 rounded-xl transition-all duration-300 border ${
            isOpen 
              ? 'bg-purple-500/20 border-purple-500/40 text-purple-300' 
              : 'bg-gray-900/60 border-gray-800 text-gray-300 hover:border-gray-700 hover:bg-gray-900'
          }`}
        >
          <FaFilter className={`text-sm transition-transform ${isOpen ? 'rotate-12' : ''}`} />
          <span className="font-semibold text-sm">Filters</span>
          {activeCount > 0 && (
            <span className="px-2 py-0.5 bg-gradient-to-r from-purple-500 to-blue-500 text-white text-xs rounded-full font-bold">
              {activeCount}
            </span>
          )}
          <FaChevronDown className={`text-xs transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {/* Quick filter chips */}
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setFilters({ ...filters, status: filters.status === 'active' ? '' : 'active' })}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              filters.status === 'active'
                ? 'bg-green-500/20 text-green-400 border border-green-500/40'
                : 'bg-gray-900/50 text-gray-400 border border-gray-800 hover:border-gray-700'
            }`}
          >
            Active Only
          </button>
          <button
            onClick={() => setFilters({ ...filters, type: filters.type === 'BAN' ? '' : 'BAN' })}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              filters.type === 'BAN'
                ? 'bg-red-500/20 text-red-400 border border-red-500/40'
                : 'bg-gray-900/50 text-gray-400 border border-gray-800 hover:border-gray-700'
            }`}
          >
            Bans
          </button>
          <button
            onClick={() => setFilters({ ...filters, type: filters.type === 'MUTE' ? '' : 'MUTE' })}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              filters.type === 'MUTE'
                ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/40'
                : 'bg-gray-900/50 text-gray-400 border border-gray-800 hover:border-gray-700'
            }`}
          >
            Mutes
          </button>
        </div>
      </div>

      {isOpen && (
        <div className="mt-4 glass-card p-6 rounded-2xl animate-fade-in">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <label className="block text-xs font-bold text-gray-500 mb-2 uppercase tracking-wider">
                Search Reason
              </label>
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm" />
                <input
                  type="text"
                  placeholder="Type to search punishments..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="w-full pl-10 pr-4 py-2.5 bg-gray-900/60 border border-gray-800 rounded-xl text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-500 mb-2 uppercase tracking-wider">
                Type
              </label>
              <select
                value={filters.type}
                onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                className="w-full px-3 py-2.5 bg-gray-900/60 border border-gray-800 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all text-sm cursor-pointer"
              >
                <option value="">All Types</option>
                <option value="BAN">🚫 Ban ({stats?.banCount || 0})</option>
                <option value="MUTE">🔇 Mute ({stats?.muteCount || 0})</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-500 mb-2 uppercase tracking-wider">
                Status
              </label>
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="w-full px-3 py-2.5 bg-gray-900/60 border border-gray-800 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all text-sm cursor-pointer"
              >
                <option value="">All Status</option>
                <option value="active">✓ Active ({stats?.activeCount || 0})</option>
                <option value="expired">✗ Expired ({(stats?.total - stats?.activeCount) || 0})</option>
              </select>
            </div>
          </div>

          {activeCount > 0 && (
            <button
              onClick={onReset}
              className="mt-4 flex items-center gap-2 px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors border border-red-500/20 text-sm font-semibold"
            >
              <FaTimes />
              <span>Clear All Filters</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// --- ENHANCED SKELETON ---
const SkeletonCard = () => (
  <div className="p-6 glass-card rounded-2xl flex items-start gap-5">
    <div className="w-14 h-14 shimmer rounded-2xl flex-shrink-0"></div>
    <div className="flex-1 space-y-4">
      <div className="flex justify-between items-center">
        <div className="flex gap-2">
          <div className="h-6 w-20 shimmer rounded-lg"></div>
          <div className="h-6 w-24 shimmer rounded-lg"></div>
        </div>
        <div className="h-6 w-24 shimmer rounded-full"></div>
      </div>
      <div className="h-16 shimmer rounded-xl"></div>
      <div className="grid grid-cols-3 gap-4">
        <div className="h-12 shimmer rounded-lg"></div>
        <div className="h-12 shimmer rounded-lg"></div>
        <div className="h-12 shimmer rounded-lg"></div>
      </div>
    </div>
  </div>
);

// --- INFO ITEM ---
const InfoItem = ({ icon: Icon, label, children }) => (
  <div className="bg-gray-900/40 rounded-lg p-3 border border-gray-800/50">
    <dt className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-1.5 flex items-center gap-1.5">
      {Icon && <Icon className="text-[10px]" />}
      {label}
    </dt>
    <dd className="text-sm font-semibold text-gray-100 break-words">{children}</dd>
  </div>
);

// --- ENHANCED PUNISHMENT CARD ---
const PunishmentCard = ({ punishment, index }) => {
  const isBan = punishment.type === "BAN";
  const Icon = isBan ? FaBan : FaVolumeMute;
  const accentColor = isBan ? "red" : "yellow";
  
  const copyId = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(punishment.id);
    toast.success("ID copied!", {
      style: { background: "#1f2937", color: "#fff", border: "1px solid #374151" },
    });
  };

  return (
    <div
      className="relative glass-card glass-card-hover rounded-2xl overflow-hidden animate-fade-in group"
      style={{ animationDelay: `${index * 0.05}s` }}
    >
      {/* Accent line */}
      <div className={`absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b ${
        isBan ? 'from-red-500 to-red-700' : 'from-yellow-500 to-orange-600'
      }`}></div>

      <div className="p-5 sm:p-6 flex flex-col sm:flex-row items-start gap-5">
        {/* Icon */}
        <div className={`relative p-4 rounded-2xl flex-shrink-0 ${
          isBan 
            ? 'bg-gradient-to-br from-red-500/20 to-red-700/10 border border-red-500/30' 
            : 'bg-gradient-to-br from-yellow-500/20 to-orange-600/10 border border-yellow-500/30'
        }`}>
          <Icon className={`text-2xl sm:text-3xl ${isBan ? 'text-red-400 glow-red' : 'text-yellow-400 glow-yellow'}`} />
          {punishment.active && (
            <span className={`absolute -top-1 -right-1 w-3 h-3 rounded-full status-dot ${
              isBan ? 'bg-red-500' : 'bg-yellow-500'
            }`}></span>
          )}
        </div>

        <div className="flex-1 w-full min-w-0">
          {/* Header */}
          <div className="flex flex-wrap justify-between items-start gap-3 mb-4">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`px-3 py-1 rounded-lg text-xs font-black uppercase tracking-wider border ${
                isBan 
                  ? 'bg-red-500/10 text-red-400 border-red-500/30' 
                  : 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30'
              }`}>
                {punishment.type}
              </span>
              <button
                onClick={copyId}
                className="group/id flex items-center gap-1.5 text-xs font-mono text-gray-500 bg-gray-900/60 hover:bg-gray-800 px-2.5 py-1 rounded-md transition-colors border border-gray-800"
                title="Click to copy full ID"
              >
                <span>#{punishment.id.substring(0, 8)}</span>
                <FaCopy className="text-[10px] opacity-0 group-hover/id:opacity-100 transition-opacity" />
              </button>
              {punishment.alt && (
                <span className="flex items-center gap-1 px-2.5 py-1 bg-orange-500/10 text-orange-400 border border-orange-500/30 rounded-md text-xs font-bold">
                  <FaUserShield className="text-[10px]" /> ALT
                </span>
              )}
            </div>
            
            <div className={`flex items-center gap-2 text-xs font-bold px-3 py-1.5 rounded-full ${
              punishment.active 
                ? 'bg-green-500/10 text-green-400 border border-green-500/30' 
                : 'bg-gray-800/60 text-gray-500 border border-gray-700/50'
            }`}>
              {punishment.active ? (
                <>
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 status-dot"></span>
                  ACTIVE
                </>
              ) : (
                <>
                  <FaTimesCircle className="text-[10px]" />
                  EXPIRED
                </>
              )}
            </div>
          </div>

          {/* Reason */}
          <div className={`relative bg-gradient-to-br ${
            isBan ? 'from-red-950/30 to-gray-900/50' : 'from-yellow-950/20 to-gray-900/50'
          } p-4 rounded-xl border border-gray-800/60 mb-4`}>
            <div className="flex items-start gap-3">
              <FaExclamationTriangle className={`text-sm flex-shrink-0 mt-0.5 ${
                isBan ? 'text-red-400' : 'text-yellow-400'
              }`} />
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-1">Reason</p>
                <p className="text-base text-white font-semibold break-words leading-snug">
                  {punishment.reason}
                </p>
              </div>
            </div>
          </div>

          {/* Details Grid */}
          <dl className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <InfoItem icon={FaCalendarAlt} label="Issued At">
              {punishment.issuedAt === 0 
                ? <span className="text-gray-500">Unknown</span>
                : new Date(punishment.issuedAt * 1000).toLocaleString()}
            </InfoItem>

            <InfoItem icon={FaClock} label="Expires At">
              {punishment.permanent || punishment.validUntil === 0
                ? <span className="inline-flex items-center gap-1 text-red-400 font-bold">
                    <FaFire className="text-xs" /> Permanent
                  </span>
                : new Date(punishment.validUntil * 1000).toLocaleString()}
            </InfoItem>

            <InfoItem icon={FaShieldAlt} label="Account Type">
              {punishment.alt ? (
                <span className="text-orange-400 font-bold">Alt Account</span>
              ) : (
                <span className="text-gray-300">Main Account</span>
              )}
            </InfoItem>
          </dl>
        </div>
      </div>
    </div>
  );
};

// --- STAT CARD ---
const StatCard = ({ icon: Icon, label, value, color, trend }) => (
  <div className="glass-card glass-card-hover p-5 rounded-2xl relative overflow-hidden group">
    <div className={`absolute -top-8 -right-8 w-32 h-32 rounded-full opacity-10 blur-2xl bg-${color}-500 group-hover:opacity-20 transition-opacity`}></div>
    <div className="relative flex items-center justify-between">
      <div>
        <p className="text-gray-500 text-[11px] font-bold uppercase tracking-widest mb-2">{label}</p>
        <p className={`text-4xl font-black text-${color}-400`}>{value}</p>
        {trend && <p className="text-xs text-gray-500 mt-2">{trend}</p>}
      </div>
      <div className={`p-3.5 bg-${color}-500/10 rounded-2xl text-${color}-400 border border-${color}-500/20`}>
        <Icon className="text-2xl" />
      </div>
    </div>
  </div>
);

// --- MAIN PAGE ---
const PunishmentsPage = () => {
  const { player } = useParams();
  const [playerData, setPlayerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ search: "", type: "", status: "" });
  const navigate = useNavigate();

  const fetchPunishments = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`https://api.ngmc.co/v1/players/${player}/punishments`);
      if (!response.ok) throw new Error("Player not found or API error");
      const data = await response.json();
      setPlayerData(data);
      toast.success(`Loaded ${data.name || player}'s record`, {
        style: { background: "#1f2937", color: "#fff", border: "1px solid #374151" },
      });
    } catch (err) {
      setError(err.message);
      toast.error(`Failed: ${err.message}`, {
        style: { background: "#7f1d1d", color: "#fff" },
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPunishments();
  }, [player]);

  const filteredPunishments = useMemo(() => {
    if (!playerData?.punishments) return [];
    return playerData.punishments.filter(p => {
      if (filters.search && !p.reason.toLowerCase().includes(filters.search.toLowerCase())) return false;
      if (filters.type && p.type !== filters.type) return false;
      if (filters.status === "active" && !p.active) return false;
      if (filters.status === "expired" && p.active) return false;
      return true;
    });
  }, [playerData, filters]);

  const stats = useMemo(() => {
    if (!playerData || !playerData.punishments) return null;
    const pun = playerData.punishments;
    const activeCount = pun.filter(p => p.active).length;
    const banCount = pun.filter(p => p.type === "BAN").length;
    const muteCount = pun.filter(p => p.type === "MUTE").length;
    const permanentCount = pun.filter(p => p.permanent).length;
    const total = pun.length || 1;
    
    const typeData = [
      { type: "Ban", count: banCount, percent: (banCount / total) * 100, color: "#ef4444" },
      { type: "Mute", count: muteCount, percent: (muteCount / total) * 100, color: "#eab308" }
    ].filter(d => d.count > 0);

    const reasonCounts = pun.reduce((acc, p) => {
      const reasonStr = p.reason || "Unknown Reason";
      acc[reasonStr] = (acc[reasonStr] || 0) + 1;
      return acc;
    }, {});
    
    const allReasons = Object.entries(reasonCounts)
      .map(([name, count]) => ({ name, count, percent: (count / total) * 100 }))
      .sort((a, b) => b.count - a.count);

    return { total: pun.length, activeCount, typeData, allReasons, banCount, muteCount, permanentCount };
  }, [playerData]);

  const resetFilters = () => {
    setFilters({ search: "", type: "", status: "" });
    toast.success("Filters cleared", {
      style: { background: "#1f2937", color: "#fff", border: "1px solid #374151" },
    });
  };

  const renderDashboard = () => {
    if (!stats || stats.total === 0) return null;

    return (
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 mb-8">
        {/* Stats Column */}
        <div className="lg:col-span-4 grid grid-cols-2 lg:grid-cols-1 gap-4 animate-fade-in">
          <StatCard 
            icon={FaHistory} 
            label="Total Punishments" 
            value={stats.total} 
            color="blue"
          />
          <StatCard 
            icon={FaExclamationTriangle} 
            label="Currently Active" 
            value={stats.activeCount} 
            color="red"
            trend={stats.activeCount > 0 ? "Requires attention" : "All clear"}
          />
          <StatCard 
            icon={FaFire} 
            label="Permanent" 
            value={stats.permanentCount} 
            color="orange"
          />
          <StatCard 
            icon={FaShieldAlt} 
            label="Clean Ratio" 
            value={`${Math.round((1 - stats.activeCount/stats.total) * 100)}%`} 
            color="green"
          />
        </div>

        {/* Donut Chart */}
        <div className="lg:col-span-4 glass-card p-6 rounded-2xl flex flex-col animate-fade-in" style={{ animationDelay: '0.1s' }}>
          <h3 className="text-white font-bold mb-4 flex items-center gap-2 text-sm uppercase tracking-wider">
            <FaChartBar className="text-purple-400" /> Distribution
          </h3>
          <div className="flex-1 flex items-center justify-center">
            <DonutChart data={stats.typeData} />
          </div>
          <div className="flex justify-center gap-4 mt-4 pt-4 border-t border-gray-800">
            {stats.typeData.map(item => (
              <div key={item.type} className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color, boxShadow: `0 0 8px ${item.color}` }}></div>
                <span className="text-xs text-gray-400 font-semibold">{item.type}</span>
                <span className="text-xs font-black text-white">{item.count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Reasons Chart */}
        <div className="lg:col-span-4 glass-card p-6 rounded-2xl flex flex-col overflow-hidden animate-fade-in" style={{ animationDelay: '0.2s' }}>
          <h3 className="text-white font-bold mb-4 flex items-center justify-between text-sm uppercase tracking-wider">
            <span className="flex items-center gap-2">
              <FaChartBar className="text-purple-400" /> Top Reasons
            </span>
            <span className="text-[10px] text-gray-500 font-mono normal-case">{stats.allReasons.length} unique</span>
          </h3>
          <ReasonBarChart reasons={stats.allReasons} />
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="space-y-4 mt-8">
          {[...Array(3)].map((_, index) => <SkeletonCard key={index} />)}
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center p-12 glass-card rounded-2xl mt-8 border border-red-900/30">
          <div className="inline-flex p-5 bg-red-500/10 rounded-2xl mb-4 border border-red-500/20">
            <FaTimesCircle className="text-5xl text-red-500" />
          </div>
          <h3 className="text-2xl font-bold text-white mb-2">Something went wrong</h3>
          <p className="text-gray-400 mb-6">{error}</p>
          <button
            onClick={fetchPunishments}
            className="px-8 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white font-bold rounded-xl shadow-lg shadow-purple-500/20 transition-all hover:scale-105"
          >
            Try Again
          </button>
        </div>
      );
    }

    if (!playerData) return null;

    if (playerData.punishments.length === 0) {
      return (
        <div className="text-center py-20 glass-card rounded-2xl mt-8 border border-green-900/30 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 to-transparent"></div>
          <div className="relative">
            <div className="inline-flex p-6 bg-green-500/10 rounded-full mb-6 border border-green-500/20 animate-float">
              <FaCheckCircle className="text-6xl text-green-400 glow-green" />
            </div>
            <h2 className="text-3xl font-black text-white mb-3">Clean Record!</h2>
            <p className="text-gray-400 text-lg">
              No punishments found for <span className="text-white font-bold">{playerData.name || player}</span>
            </p>
          </div>
        </div>
      );
    }

    return (
      <>
        {renderDashboard()}
        
        <FilterPanel 
          filters={filters} 
          setFilters={setFilters} 
          stats={stats}
          onReset={resetFilters}
        />

        <div className="mb-5 flex items-center justify-between flex-wrap gap-3">
          <h3 className="text-2xl font-black text-white flex items-center gap-3">
            <FaHistory className="text-purple-400" /> 
            Punishment History
          </h3>
          <div className="flex items-center gap-2">
            <span className="px-4 py-1.5 bg-gray-900/60 border border-gray-800 text-gray-300 text-xs rounded-full font-bold">
              <span className="text-white">{filteredPunishments.length}</span>
              <span className="text-gray-500"> of {playerData.punishments.length}</span>
            </span>
          </div>
        </div>

        {filteredPunishments.length === 0 ? (
          <div className="text-center py-16 glass-card rounded-2xl">
            <FaSearch className="mx-auto text-5xl text-gray-700 mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">No Results Found</h3>
            <p className="text-gray-400 mb-6">Try adjusting your filters</p>
            <button
              onClick={resetFilters}
              className="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white rounded-xl transition-all font-bold"
            >
              Clear Filters
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredPunishments.map((punishment, index) => (
              <PunishmentCard
                key={punishment.id}
                punishment={punishment}
                index={index}
              />
            ))}
          </div>
        )}
      </>
    );
  };

  return (
    <>
      <PageStyles />
      <div className="min-h-screen bg-[#0a0a0f] noise-bg text-gray-100 p-4 sm:p-6 lg:p-8 font-sans">
        <Toaster position="top-right" />
        
        <div className="max-w-7xl mx-auto">
          {/* Back button */}
          <button
            onClick={() => navigate(-1)}
            className="mb-6 flex items-center gap-2 text-gray-500 hover:text-white transition-colors text-sm font-semibold group"
          >
            <FaArrowLeft className="group-hover:-translate-x-1 transition-transform" />
            Back
          </button>

          {/* Header */}
          <div className="glass-card rounded-3xl p-6 sm:p-8 mb-8 animate-fade-in relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 via-transparent to-blue-500/5"></div>
            <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-6">
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500 to-blue-500 rounded-2xl blur-lg opacity-40 group-hover:opacity-60 transition-opacity"></div>
                <div className="relative w-20 h-20 sm:w-24 sm:h-24 rounded-2xl overflow-hidden border-2 border-gray-700 bg-gray-800">
                  <img 
                    src={`https://minotar.net/helm/${playerData?.name || player}/100.png`} 
                    alt={`${player} avatar`}
                    onError={(e) => { e.target.src = 'https://minotar.net/helm/MHF_Steve/100.png'; }}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              
              <div className="flex-1">
                <p className="text-xs font-bold text-purple-400 uppercase tracking-widest mb-2">Player Profile</p>
                <h1 className="text-3xl sm:text-5xl font-black tracking-tight mb-2">
                  {loading ? (
                    <span className="inline-block w-48 h-12 shimmer rounded-lg"></span>
                  ) : (
                    <span className="bg-gradient-to-br from-white via-gray-100 to-gray-400 bg-clip-text text-transparent">
                      {playerData?.name || "Not Found"}
                    </span>
                  )}
                </h1>
                <button
                  onClick={() => navigate(`/players/${playerData?.name || "Not Found"}`)}
                  className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white font-semibold group transition-colors"
                >
                  <FaEye className="text-xs" />
                  <span>View full profile</span>
                  <span className="group-hover:translate-x-1 transition-transform">→</span>
                </button>
              </div>

              {stats && stats.total > 0 && (
                <div className="hidden sm:flex flex-col items-end gap-1 px-5 py-3 bg-gray-900/60 rounded-2xl border border-gray-800">
                  <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">Record</span>
                  <span className={`text-2xl font-black ${stats.activeCount > 0 ? 'text-red-400' : 'text-green-400'}`}>
                    {stats.activeCount > 0 ? 'Flagged' : 'Good'}
                  </span>
                </div>
              )}
            </div>
          </div>

          {renderContent()}
        </div>
      </div>
    </>
  );
};

export default PunishmentsPage;
